/**
 * InsightCard Component
 *
 * A flexible card layout for displaying insights in two modes:
 * 1. Two-column layout (when both leftSlot and rightSlot are provided):
 *    - Left column: Primary content (typically a chart)
 *    - Right column: Supporting content stacked vertically (text, metrics, CTAs)
 * 2. Single-column layout (when children is provided):
 *    - Content flows top to bottom in a single column
 *
 * Handles all layout concerns (padding, grid, border styling) internally.
 */

import React from 'react';
import { Badge, Card } from '@strivelabs/ui-core';
import { RichTextRenderer } from '../rich-text';

export type InsightCardBorderColor = 'red' | 'green' | 'yellow' | 'gray' | 'blue' | 'purple';

export interface InsightCardProps {
  /** Optional badge pills displayed above the heading (e.g., campaign name, region) */
  tags?: string[];
  /** Card heading - supports markdown for bold/italic text */
  heading?: string;
  /** Optional badge shown next to heading */
  headingBadge?: string;
  /** Left border color indicating severity/status (optional) */
  borderColor?: InsightCardBorderColor;
  /** Content for left column (for 2-column mode) */
  leftSlot?: React.ReactNode;
  /** Content for right column (for 2-column mode) */
  rightSlot?: React.ReactNode;
  /** Content for single-column mode (flows top to bottom) */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

const getBorderColorClass = (borderColor?: InsightCardBorderColor): string => {
  if (!borderColor) return '';
  const borderColorMap: Record<InsightCardBorderColor, string> = {
    red: 'border-l-4 border-l-red-600',
    green: 'border-l-4 border-l-green-600',
    yellow: 'border-l-4 border-l-yellow-600',
    gray: 'border-l-4 border-l-gray-600',
    blue: 'border-l-4 border-l-blue-600',
    purple: 'border-l-4 border-l-purple-600',
  };
  return borderColorMap[borderColor];
};

const CardComponent = Card as React.ComponentType<React.PropsWithChildren<{ className?: string }>>;

export const InsightCard: React.FC<InsightCardProps> = ({
  tags,
  heading,
  headingBadge,
  borderColor,
  leftSlot,
  rightSlot,
  children,
  className,
}) => {
  const borderColorClass = getBorderColorClass(borderColor);

  // Determine which mode to use:
  // - Two-column mode: both leftSlot and rightSlot are provided
  // - Single-column mode: children is provided (leftSlot/rightSlot not provided)
  const isTwoColumnMode = leftSlot !== undefined && rightSlot !== undefined;

  return (
    <CardComponent className={`w-full p-4 md:p-6 ${borderColorClass} ${className ?? ''}`}>
      {/* Heading section */}
      {(tags?.length || heading) && (
        <div className="mb-12 flex flex-col gap-2">
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag, index) => (
                <Badge key={`${tag}-${index}`} variant="customGray" className="uppercase text-[10px] font-normal">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
          {heading && (
            <div className="flex items-center gap-3">
              <RichTextRenderer
                content={heading}
                className="[&>p]:text-[28px] [&>p]:leading-tight [&>p]:font-medium text-custom-text-secondary"
              />
              {headingBadge && (
                <Badge variant="customGray" className="flex-shrink-0">
                  {headingBadge}
                </Badge>
              )}
            </div>
          )}
        </div>
      )}

      {/* Render based on mode */}
      {isTwoColumnMode ? (
        // Two-column grid layout
        <div className="grid grid-cols-2 gap-8">
          {/* Left column */}
          <div className="flex flex-col">
            {leftSlot}
          </div>

          {/* Right column - stacked vertically */}
          <div className="flex flex-col gap-4">
            {rightSlot}
          </div>
        </div>
      ) : (
        // Single-column flow layout
        <div className="flex flex-col gap-4">
          {children}
        </div>
      )}
    </CardComponent>
  );
};

export default InsightCard;
